import React, { useState, useCallback, useEffect } from "react";
import { SortOrder, TableColumn, TableRow } from "./Table.types";

export interface useTableProps {
  data?: TableRow[];
  columns: TableColumn[];
  initialSortOrder?: SortOrder;
  rowsPerPageOptions?: number[];
  intialOrderByColumnId?: string;
  initialPage?: number;
}

export const useTable = ({
  columns,
  data,
  initialSortOrder,
  intialOrderByColumnId,
  initialPage,
  rowsPerPageOptions = [10, 25, 50, 100],
}: useTableProps) => {
  const [rows, setRows] = useState<TableRow[]>([]);
  const [rowsError, setRowsError] = useState("");
  const [isFetchingRows, setIsFetchingRows] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TableRow | undefined>();
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    initialSortOrder ?? "asc"
  );
  const [orderByColumnId, setOrderByColumnId] = useState(
    intialOrderByColumnId ?? ""
  );
  const [page, setPage] = useState(initialPage ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    if (data) {
      setRows(data);
    }
  }, [data]);

  const onSortOrderChange = useCallback(
    (property: string) => (_event: React.MouseEvent<HTMLSpanElement>) => {
      const isAsc = orderByColumnId === property && sortOrder === "asc";
      const direction = !isAsc ? "asc" : "desc";

      setSortOrder(direction);
      setOrderByColumnId(property);
      setSelectedRow(undefined);
      setPage(0);
    },
    [orderByColumnId, sortOrder]
  );

  const onRowSelect = useCallback(
    (row: TableRow) => (_event: React.MouseEvent<HTMLTableRowElement>) => {
      if (row.id === selectedRow?.id) {
        setSelectedRow(undefined);
      } else {
        setSelectedRow(row);
      }
    },
    [selectedRow?.id]
  );

  const onPageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setSelectedRow(undefined);
      setPage(newPage);
    },
    []
  );

  const onRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const size = parseInt(event.target.value, 10);
      setRowsPerPage(size);
      setSelectedRow(undefined);
      setPage(0);
    },
    []
  );

  const resetAll = useCallback(() => {
    setPage(0);
    setSelectedRow(undefined);
    setSortOrder("asc");
    setOrderByColumnId(columns[0].id);
  }, [columns]);

  return {
    resetAll,
    rows,
    columns,
    orderByColumnId,
    selectedRow,
    setSelectedRow,
    sortOrder,
    setSortOrder,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalRows,
    setTotalRows,
    onSortOrderChange,
    onRowSelect,
    onPageChange,
    onRowsPerPageChange,
    isFetchingRows,
    rowsError,
    setIsFetchingRows,
    setRowsError,
    setRows,
  };
};
